/**
 * Read-only markdown renderer, used for backlog item bodies.
 *
 * Lazy for the same reason as {@link MarkdownEditor}: react-markdown and
 * remark-gfm together are a sizeable chunk and only the backlog pages need
 * them. Both are pulled inside the same {@link lazy} call so they land in one
 * chunk — importing remark-gfm at module scope would drag it into the main
 * bundle just to pass it as a prop.
 *
 * Raw HTML is deliberately not rendered — there is no ``rehype-raw`` here.
 * Bodies come back from GitHub issues, so they are third-party content, and
 * the default react-markdown pipeline escapes embedded HTML and sanitises
 * link targets on its own. A useful side effect: the
 * ``<!-- backlog-source: … -->`` marker that the sync writes at the top of
 * every body stays invisible in the preview.
 */

import { Suspense, lazy } from "react";
import type { Components } from "react-markdown";

import { Spinner } from "@/components/Spinner";

const COMPONENTS: Components = {
  // Backlog bodies link out to GitHub, runbooks and dashboards. Without this
  // every link would replace the SPA in place and lose unsaved edits.
  a: ({ children, ...props }) => (
    <a {...props} target="_blank" rel="noreferrer">
      {children}
    </a>
  ),
  // Wide tables are common in these bodies (cost breakdowns, ARN lists); let
  // them scroll rather than blow out the page width.
  table: ({ children, ...props }) => (
    <div className="overflow-x-auto">
      <table {...props}>{children}</table>
    </div>
  ),
};

const Renderer = lazy(async () => {
  const [{ default: ReactMarkdown }, { default: remarkGfm }] = await Promise.all([
    import("react-markdown"),
    import("remark-gfm"),
  ]);
  return {
    default: ({ value }: { value: string }) => (
      // ``skipHtml`` drops HTML nodes entirely. Without it react-markdown
      // escapes them and renders them as visible text, which would put the
      // ``<!-- backlog-source: … -->`` marker at the top of every preview.
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={COMPONENTS}
        skipHtml
      >
        {value}
      </ReactMarkdown>
    ),
  };
});

export interface MarkdownViewProps {
  value: string;
  /** Extra classes for the wrapper, typically sizing. */
  className?: string;
}

/**
 * ``max-w-none`` matters: the typography plugin targets article layouts and
 * caps at ~65ch by default, which would leave half of this admin page empty.
 * ``prose-sm`` likewise brings the line height back to the density of the
 * surrounding forms.
 */
const PROSE =
  "prose prose-sm prose-ink max-w-none " +
  "prose-headings:text-ink prose-headings:font-semibold " +
  "prose-a:text-brand-600 prose-a:no-underline prose-a:hover:underline " +
  "prose-code:rounded prose-code:bg-sunken prose-code:px-1 prose-code:py-0.5 " +
  "prose-code:font-normal prose-code:text-brand-700 " +
  // The plugin decorates inline code with literal backticks; drop them, the
  // background already carries the meaning.
  "prose-code:before:content-none prose-code:after:content-none " +
  "prose-pre:bg-inverse prose-pre:text-on-fill " +
  // ``prose-code:`` matches every <code>, including the ones inside <pre>,
  // which would paint a light chip behind each line of a dark code block.
  // Reset them back to the block's own colours.
  "[&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-inherit " +
  "prose-blockquote:border-l-brand-300 prose-blockquote:not-italic " +
  // Typography wraps blockquotes in curly quotes via ::before/::after. These
  // bodies use blockquotes as status callouts, not as citations, so the
  // quotes read as a stray character.
  "[&_blockquote_p:first-of-type]:before:content-none " +
  "[&_blockquote_p:last-of-type]:after:content-none " +
  "prose-li:marker:text-ink-muted " +
  "prose-th:text-ink prose-hr:border-line " +
  "prose-img:rounded";

export function MarkdownView({ value, className = "" }: MarkdownViewProps) {
  if (!value.trim()) {
    return (
      <div className={`p-4 text-sm italic text-ink-muted ${className}`}>
        No content to display.
      </div>
    );
  }

  return (
    <article className={`${PROSE} ${className}`}>
      <Suspense fallback={<Spinner label="Rendering markdown…" />}>
        <Renderer value={value} />
      </Suspense>
    </article>
  );
}
